import React from 'react';
import { ShieldCheck, Music, Target, Globe, AlertTriangle } from 'lucide-react';

export default function BrandingLaws() {
  const laws = [
    {
      title: "The Rhythm Rule",
      original: "Syllable Tier Priority",
      icon: Music,
      layman: "Think of your name like a drumbeat. Short beats (1-2 syllables) are powerful and easy to remember. Longer beats (4-5 syllables) are informative but harder to scream over the noise of the market.",
      stars: [
        { label: "1-2 Beats", stars: "5 Stars", desc: "Premium, Iconic status" },
        { label: "3 Beats", stars: "3 Stars", desc: "Balanced, Descriptive" },
        { label: "4+ Beats", stars: "1-2 Stars", desc: "Technical, Complex" }
      ]
    },
    {
      title: "The Anchor Prefix",
      original: "Brand Consistency",
      icon: Anchor,
      layman: "We use 'KR' as an anchor. It tells the world who the family is (Kova + Rand York). A good brand name should sail away from the anchor but always stay connected to it.",
      note: "Used for standalone checks (Track A) to ensure parent legacy."
    },
    {
      title: "Market Mirroring",
      original: "Linguistic Neutrality",
      icon: Target,
      layman: "A name must be a clean mirror. We check for 'Negative Words'—hidden meanings in other languages—to make sure your mirror doesn't show a cracks when seen in France, India, or Germany.",
      warning: "Essential for international joint ventures."
    },
    {
      title: "Digital Real Estate",
      original: "4-Column Domain Audit",
      icon: Globe,
      layman: "Owning the land is better than renting it. We search for .com and .in domains to ensure you own the 'online address' of your brand name before you invest in its construction.",
      logic: "Primary: name.com | Secondary: krname.com"
    }
  ];

  return (
    <div className="min-h-screen bg-industrial-900 text-white p-12">
      <div className="max-w-4xl mx-auto">
        <header className="mb-16">
          <h1 className="text-4xl font-bold mb-4 tracking-tight">The Laws of Branding</h1>
          <p className="text-industrial-400 text-xl font-light">
            Our strategic framework for engineering iconic identities, translated for layman understanding.
          </p>
        </header>

        <div className="space-y-12">
          {laws.map((law, i) => (
            <div key={i} className="glass-card p-10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <law.icon className="w-32 h-32" />
              </div>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-brand/10 rounded-xl">
                  <law.icon className="w-8 h-8 text-brand" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{law.title}</h3>
                  <p className="text-xs font-mono text-industrial-500 uppercase tracking-widest">Global Protocol: {law.original}</p>
                </div>
              </div>

              <p className="text-lg text-slate-300 mb-8 leading-relaxed font-light italic border-l-2 border-brand/30 pl-6">
                "{law.layman}"
              </p>

              {law.stars && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {law.stars.map((s, idx) => (
                    <div key={idx} className="bg-industrial-950/50 p-4 rounded-lg border border-industrial-800">
                      <div className="text-sm font-bold text-white mb-1">{s.label}</div>
                      <div className="text-brand text-xs mb-1 font-mono uppercase">{s.stars}</div>
                      <div className="text-[10px] text-industrial-500">{s.desc}</div>
                    </div>
                  ))}
                </div>
              )}

              {law.logic && (
                <div className="inline-block bg-brand/5 border border-brand/20 px-4 py-2 rounded text-xs text-brand font-mono">
                  Engine Logic: {law.logic}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Anchor = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12H2"/><path d="M22 12h-3"/><path d="M12 2v10"/><path d="m12 16 3 3-3 3-3-3 3-3Z"/><path d="M8 22a4 4 0 0 1-8-4"/><path d="M16 22a4 4 0 0 0 8-4"/></svg>
);
